import type { ObraIdentificavel } from "@/shared/utils/formatar-identificacao-obra";
import {
  calcularConflitos,
  conflitoKey,
  isObraCancelada as isObraCanceladaCarga,
  overlaps,
  separarConflitos,
} from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoAlocacao,
  CargaTrabalhoIntervalo,
  CargaTrabalhoObrasData,
  CargaTrabalhoProfissionaisData,
} from "@/modules/carga-trabalho";
import {
  conflitosAbertos as conflitosLocalAbertos,
  conflitosDoLocal,
  conflitosPorAlocacaoDeLocal,
} from "@/modules/carga-de-trabalho";
import type {
  AlocacoesPorLocal,
  AlocacoesPorObra,
  ConflitoLocal,
} from "@/modules/carga-de-trabalho";
import { findConflitos } from "../carga-de-trabalho.model";
import type { Profissional } from "../profissionais.types";
import { isObraCancelada } from "../recursos.types";
import type {
  AlocacaoDoFornecedor,
  FornecedorAlocacoes,
  FornecedorConflitoIgnorado,
  ObraAlocacoes,
} from "../recursos.types";
import { alocacaoSimuladaId, membroObraKey } from "./simulacao.aplicar";
import { alteracoesDaAcao, paresComEfeito } from "./simulacao.model";
import type { AlteracaoSimulada } from "./simulacao.model";
import type { CenarioSimulacao, ConflitoNovo, ConflitoNovoItem } from "./simulacao.model";
import { rotuloObra } from "./simulacao.textos";

/**
 * Conflitos do Modo Simulação (HUs 2225 e 2224): os que uma alteração cria sobre os dados
 * de cada perspectiva (RN011), as ausências programadas e a disponibilidade simulada (RN14).
 */

/*
 * Conflitos em aberto, cada um por uma chave estável (recurso + par de alocações) e com
 * a descrição do modal de justificativa (RN011): o que a alteração criou é o que está
 * depois e não estava antes.
 */

export type ConflitosAbertos = ReadonlyMap<string, ConflitoNovo>;

export function novosConflitos(antes: ConflitosAbertos, depois: ConflitosAbertos): ConflitoNovo[] {
  return [...depois].filter(([chave]) => !antes.has(chave)).map(([, conflito]) => conflito);
}

function item(
  obra: ObraIdentificavel & { nome: string },
  intervalo: CargaTrabalhoIntervalo & { id?: string },
): ConflitoNovoItem {
  return {
    rotulo: rotuloObra(obra),
    obraCodigo: obra.codigo,
    ...(intervalo.id ? { alocacaoId: intervalo.id } : {}),
    inicio: intervalo.inicio,
    fim: intervalo.fim,
  };
}

/**
 * Ids das alocações que a alteração cria ou move (nas visões por recurso e por obra): no
 * modal de RN011 a "Nova alocação" é uma delas, e a "Obra atual" é a outra do conflito,
 * mesmo quando as duas são da mesma obra.
 */
export function alocacoesDaAlteracao(alteracao: AlteracaoSimulada): string[] {
  switch (alteracao.acao) {
    case "definir-equipe":
      return paresComEfeito(alteracao.anterior, alteracao.novo).flatMap(
        ({ membro, previa, index }) => [
          membro.alocacaoId ?? previa?.alocacaoId ?? alocacaoSimuladaId(alteracao, membro, index),
          membroObraKey(alteracao.obra.id, membro.usuarioId),
        ],
      );
    case "remanejar-profissional":
      return [alteracao.alocacaoId, membroObraKey(alteracao.obra.id, alteracao.novo.id)];
    case "remanejar-fornecedor":
    case "remanejar-local":
      return [alteracao.alocacaoId];
    default:
      return [];
  }
}

export const AUSENCIA_PROGRAMADA = "Ausência programada";

/** Ausências do profissional que cruzam a alocação: também pedem justificativa (RN011). */
export function ausenciasNaAlocacao(
  recurso: string,
  alocacao: { obra: { codigo: string; nome: string } } & CargaTrabalhoIntervalo,
  ausencias: CargaTrabalhoIntervalo[],
): ConflitoNovo[] {
  return ausencias
    .filter((ausencia) => overlaps(ausencia, alocacao))
    .map((ausencia) => ({
      recurso,
      itens: [item(alocacao.obra, alocacao), { rotulo: AUSENCIA_PROGRAMADA, ...ausencia }],
    }));
}

/** Ausências programadas (indisponibilidade) do profissional escolhido, no formato do RN011. */
export function ausenciasDoProfissional(
  profissional: Profissional,
  alocacao: { obra: { codigo: string; nome: string } } & CargaTrabalhoIntervalo,
): ConflitoNovo[] {
  return ausenciasNaAlocacao(
    profissional.nome || profissional.email,
    alocacao,
    profissional.indisponibilidade.periodos,
  );
}

export function conflitosProfissionais(data: CargaTrabalhoProfissionaisData): ConflitosAbertos {
  const result = new Map<string, ConflitoNovo>();
  for (const profissional of data.profissionais) {
    const { abertos } = separarConflitos(
      calcularConflitos(profissional.alocacoes),
      profissional.conflitosIgnorados,
    );
    for (const conflito of abertos) {
      result.set(`${profissional.id}:${conflitoKey([conflito.a.id, conflito.b.id])}`, {
        recurso: profissional.nome,
        itens: [item(conflito.a.obra, conflito.a), item(conflito.b.obra, conflito.b)],
      });
    }
    for (const alocacao of profissional.alocacoes) {
      if (isObraCanceladaCarga(alocacao.obra)) continue;
      ausenciasNaAlocacao(profissional.nome, alocacao, profissional.indisponibilidades).forEach(
        (conflito) =>
          result.set(
            `${profissional.id}:ausencia:${alocacao.id}:${conflito.itens[1].inicio}`,
            conflito,
          ),
      );
    }
  }
  return result;
}

/** Na visão por Obras o conflito continua sendo do profissional, em todas as obras. */
export function conflitosObrasProfissionais(data: CargaTrabalhoObrasData): ConflitosAbertos {
  const porProfissional = new Map<string, { nome: string; alocacoes: CargaTrabalhoAlocacao[] }>();
  for (const obra of data.obras) {
    for (const profissional of obra.profissionais) {
      const atual = porProfissional.get(profissional.id) ?? {
        nome: profissional.nome,
        alocacoes: [],
      };
      atual.alocacoes.push({
        id: profissional.alocacaoId ?? membroObraKey(obra.id, profissional.id),
        papelObraId: profissional.papelObraId,
        inicio: profissional.inicio,
        fim: profissional.fim,
        obra,
      });
      porProfissional.set(profissional.id, atual);
    }
  }

  const result = new Map<string, ConflitoNovo>();
  for (const [id, { nome, alocacoes }] of porProfissional) {
    for (const conflito of calcularConflitos(alocacoes)) {
      result.set(`${id}:${conflitoKey([conflito.a.id, conflito.b.id])}`, {
        recurso: nome,
        itens: [item(conflito.a.obra, conflito.a), item(conflito.b.obra, conflito.b)],
      });
    }
  }
  return result;
}

/** Pares em aberto de um fornecedor, pela regra do Gantt de Fornecedor (HU 2041). */
function conflitosDeUmFornecedor(
  fornecedor: { id: string; nome: string },
  alocacoes: AlocacaoDoFornecedor[],
  ignorados: Pick<FornecedorConflitoIgnorado, "alocacaoIds">[],
  result: Map<string, ConflitoNovo>,
) {
  const porId = new Map(alocacoes.map((alocacao) => [alocacao.id, alocacao]));
  const pares = findConflitos(
    alocacoes.map((alocacao) => ({
      id: alocacao.id,
      inicio: alocacao.inicio,
      fim: alocacao.fim,
      cancelada: isObraCancelada(alocacao.obra.situacao),
      obraId: alocacao.obra.id,
    })),
  );

  for (const par of pares) {
    // Tratado: o par inteiro pertence a um mesmo conflito ignorado.
    if (
      ignorados.some(({ alocacaoIds }) => par.alocacoes.every((id) => alocacaoIds.includes(id)))
    ) {
      continue;
    }
    const [a, b] = par.alocacoes.map((id) => porId.get(id));
    if (!a || !b) continue;
    result.set(`${fornecedor.id}:${conflitoKey(par.alocacoes)}`, {
      recurso: fornecedor.nome,
      itens: [item(a.obra, a), item(b.obra, b)],
    });
  }
}

export function conflitosFornecedores(fornecedores: FornecedorAlocacoes[]): ConflitosAbertos {
  const result = new Map<string, ConflitoNovo>();
  for (const fornecedor of fornecedores) {
    conflitosDeUmFornecedor(
      { id: fornecedor.id || fornecedor.nome, nome: fornecedor.nome },
      fornecedor.alocacoes,
      fornecedor.conflitosIgnorados ?? [],
      result,
    );
  }
  return result;
}

export function conflitosObrasFornecedor(obras: ObraAlocacoes[]): ConflitosAbertos {
  const porFornecedor = new Map<
    string,
    { fornecedor: { id: string; nome: string }; alocacoes: AlocacaoDoFornecedor[] }
  >();
  const ignorados = new Map<string, FornecedorConflitoIgnorado>();

  for (const obra of obras) {
    const { alocacoes, conflitosIgnorados = [], ...obraResumo } = obra;
    conflitosIgnorados.forEach((ignorado) => ignorados.set(ignorado.id, ignorado));
    for (const alocacao of alocacoes) {
      const key = alocacao.fornecedor.id || alocacao.fornecedor.nome;
      const atual = porFornecedor.get(key) ?? {
        fornecedor: { id: key, nome: alocacao.fornecedor.nome },
        alocacoes: [],
      };
      atual.alocacoes.push({
        id: alocacao.id,
        inicio: alocacao.inicio,
        fim: alocacao.fim,
        obra: obraResumo,
      });
      porFornecedor.set(key, atual);
    }
  }

  const result = new Map<string, ConflitoNovo>();
  for (const { fornecedor, alocacoes } of porFornecedor.values()) {
    conflitosDeUmFornecedor(fornecedor, alocacoes, [...ignorados.values()], result);
  }
  return result;
}

function conflitoLocalNovo(recurso: string, conflito: ConflitoLocal): ConflitoNovo {
  return { recurso, itens: [item(conflito.a.obra, conflito.a), item(conflito.b.obra, conflito.b)] };
}

export function conflitosLocais(data: AlocacoesPorLocal): ConflitosAbertos {
  const result = new Map<string, ConflitoNovo>();
  for (const local of data.locais) {
    for (const conflito of conflitosLocalAbertos(
      conflitosDoLocal(local.alocacoes, data.conflitosIgnorados),
    )) {
      result.set(
        `${local.id}:${conflitoKey([conflito.a.id, conflito.b.id])}`,
        conflitoLocalNovo(local.nome, conflito),
      );
    }
  }
  return result;
}

export function conflitosObrasLocal(data: AlocacoesPorObra): ConflitosAbertos {
  const localDaAlocacao = new Map(
    data.obras.flatMap((obra) => obra.alocacoes.map((alocacao) => [alocacao.id, alocacao.local])),
  );
  const result = new Map<string, ConflitoNovo>();
  for (const conflitos of conflitosPorAlocacaoDeLocal(data.obras).values()) {
    for (const conflito of conflitosLocalAbertos(conflitos)) {
      const local = localDaAlocacao.get(conflito.a.id);
      result.set(
        `${local?.id ?? ""}:${conflitoKey([conflito.a.id, conflito.b.id])}`,
        conflitoLocalNovo(local?.nome ?? "", conflito),
      );
    }
  }
  return result;
}

/* RN14: disponibilidade no Definir Equipe considera as alocações já simuladas. */

/**
 * O profissional que está numa equipe simulada de outra obra, ou que substituiu alguém
 * num remanejamento simulado, num período que cruza o da obra em edição, aparece como
 * indisponível, com esse período somado aos dele.
 */
export function aplicarDisponibilidadeSimulada(
  profissionais: Profissional[],
  cenario: CenarioSimulacao,
  obra: { id: string } & CargaTrabalhoIntervalo,
): Profissional[] {
  const ocupados = new Map<string, CargaTrabalhoIntervalo[]>();
  const ocupar = (id: string, intervalo: CargaTrabalhoIntervalo) => {
    if (!overlaps(intervalo, obra)) return;
    ocupados.set(id, [
      ...(ocupados.get(id) ?? []),
      { inicio: intervalo.inicio, fim: intervalo.fim },
    ]);
  };

  for (const alteracao of alteracoesDaAcao(cenario, "definir-equipe")) {
    if (alteracao.obra.id === obra.id || isObraCanceladaCarga(alteracao.obra)) continue;
    alteracao.novo.forEach((membro) => ocupar(membro.usuarioId, membro));
  }
  for (const alteracao of alteracoesDaAcao(cenario, "remanejar-profissional")) {
    if (alteracao.obra.id === obra.id || isObraCancelada(alteracao.obra.situacao)) continue;
    ocupar(alteracao.novo.id, alteracao.periodo);
  }

  return profissionais.map((profissional) => {
    const periodos = ocupados.get(profissional.id);
    return periodos
      ? {
          ...profissional,
          indisponibilidade: {
            indisponivel: true,
            periodos: [...profissional.indisponibilidade.periodos, ...periodos],
          },
        }
      : profissional;
  });
}
